/**
 * Structured logging with pino.
 *
 * Produces structured JSON log output in the browser, with automatic
 * forwarding to Sentry for observability.
 *
 * Console output is enabled in development by default.
 * To enable console output in production, run in browser console:
 *   window.enableDebugLogs()
 *
 * To disable:
 *   window.disableDebugLogs()
 */

import pino from 'pino';
import * as Sentry from '@sentry/react';
import { getCorrelationId } from '@/lib/correlationId';

const isDev = import.meta.env.MODE === 'development';
const DEBUG_KEY = 'systemdox_debug';

// Check if debug is enabled (dev mode OR localStorage flag)
const isDebugEnabled = (): boolean => {
  if (isDev) return true;
  try {
    return localStorage.getItem(DEBUG_KEY) === 'true';
  } catch {
    return false;
  }
};

// Expose global functions to enable/disable debug in production
if (typeof window !== 'undefined') {
  (window as Window & { enableDebugLogs?: () => void; disableDebugLogs?: () => void }).enableDebugLogs = () => {
    localStorage.setItem(DEBUG_KEY, 'true');
    console.log('Debug logs enabled. Refresh the page to see logs.');
  };
  (window as Window & { enableDebugLogs?: () => void; disableDebugLogs?: () => void }).disableDebugLogs = () => {
    localStorage.removeItem(DEBUG_KEY);
    console.log('Debug logs disabled.');
  };
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// Map pino numeric levels to labels for Sentry forwarding
const levelToLabel: Record<number, LogLevel> = {
  10: 'debug',
  20: 'debug',
  30: 'info',
  40: 'warn',
  50: 'error',
  60: 'error',
};

// Dev log bridge: POST structured JSON to Vite dev server → terminal [FE] output
const sendToDevBridge = isDev
  ? (level: string, message: string, attrs: Record<string, unknown>) => {
      const entry = {
        level: level.toUpperCase(),
        message,
        timestamp: new Date().toISOString(),
        service: 'systemdox-frontend',
        module: attrs.component ?? undefined,
        correlationId: getCorrelationId(),
        ...attrs,
      };
      // Fire-and-forget — don't block the UI thread
      fetch('/__dev_log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      }).catch(() => { /* ignore dev bridge errors */ });
    }
  : null;

const baseLogger = pino({
  level: isDebugEnabled() ? 'debug' : 'info',
  browser: {
    asObject: true,
    write: {
      debug: (o: object) => { if (isDebugEnabled()) console.log(o); },
      info:  (o: object) => { if (isDebugEnabled()) console.info(o); },
      warn:  (o: object) => { console.warn(o); },
      error: (o: object) => { console.error(o); },
    },
    transmit: {
      level: 'info',
      send: (_level, logEvent) => {
        // Forward to Sentry structured logs
        // pino transmit: messages[0] may be merge object, messages[1] the string
        const raw = logEvent.messages ?? [];
        const msg = String(
          raw.length > 1 && typeof raw[1] === 'string' ? raw[1]
          : typeof raw[0] === 'string' ? raw[0]
          : (raw[0] as Record<string, unknown>)?.msg ?? ''
        );
        const attrs: Record<string, unknown> = logEvent.bindings?.reduce(
          (acc: Record<string, unknown>, b: Record<string, unknown>) => ({ ...acc, ...b }), {}
        ) ?? {};
        // Merge the context object (first arg if it's an object) into attrs
        if (raw.length > 1 && typeof raw[0] === 'object' && raw[0] !== null) {
          Object.assign(attrs, raw[0]);
        }
        attrs.correlationId = getCorrelationId();

        const severity = levelToLabel[logEvent.level.value] ?? 'info';
        if (severity === 'error') Sentry.logger.error(msg, attrs);
        else if (severity === 'warn') Sentry.logger.warn(msg, attrs);
        else Sentry.logger.info(msg, attrs);

        // Also send to terminal via Vite dev log bridge
        sendToDevBridge?.(severity, msg, attrs);
      },
    },
  },
});

interface LogContext {
  component?: string;
  action?: string;
  [key: string]: unknown;
}

/**
 * Debug log
 */
export const logDebug = (message: string, context?: LogContext): void => {
  baseLogger.debug(context ?? {}, message);
};

/**
 * Info log
 */
export const logInfo = (message: string, context?: LogContext): void => {
  baseLogger.info(context ?? {}, message);
};

/**
 * Warning log
 */
export const logWarn = (message: string, context?: LogContext): void => {
  baseLogger.warn(context ?? {}, message);
};

/**
 * Error log
 */
export const logError = (message: string, error?: Error, context?: LogContext): void => {
  baseLogger.error({ ...context, err: error }, message);
};

/**
 * Create a logger instance for a specific component.
 *
 * Returns the same API shape as the previous Sentry-based logger
 * so consuming files need no changes.
 */
export const createLogger = (component: string) => ({
  debug: (message: string, context?: Omit<LogContext, 'component'>) =>
    logDebug(message, { component, ...context }),
  info: (message: string, context?: Omit<LogContext, 'component'>) =>
    logInfo(message, { component, ...context }),
  warn: (message: string, context?: Omit<LogContext, 'component'>) =>
    logWarn(message, { component, ...context }),
  error: (message: string, error?: Error, context?: Omit<LogContext, 'component'>) =>
    logError(message, error, { component, ...context }),
});
